import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  CategoryDeleteImpact,
  SettingsService,
} from '../../core/services/settings.service';
import { Category } from '../../core/models/settings.models';
import { AuthService } from '../auth/auth.service';

@Component({
  selector: 'app-categories',
  standalone: true,
  imports: [FormsModule],
  templateUrl: './categories.component.html',
  styleUrls: ['../../settings-shared.css', './categories.component.css'],
})
export class CategoriesComponent implements OnInit {
  private readonly settings = inject(SettingsService);
  protected readonly auth = inject(AuthService);

  protected readonly showModal = signal(false);
  protected readonly editingId = signal<string | null>(null);
  protected readonly isEditing = computed(() => this.editingId() !== null);
  protected readonly showToast = signal(false);
  protected readonly toastMessage = signal('');
  protected readonly toastIsError = signal(false);

  protected readonly showDeleteModal = signal(false);
  protected readonly deleteLoading = signal(false);
  protected readonly deleteSubmitting = signal(false);
  protected readonly deleteTarget = signal<Category | null>(null);
  protected readonly deleteImpact = signal<CategoryDeleteImpact | null>(null);
  protected readonly deleteError = signal('');
  protected targetItemCategoryId = '';

  protected categoryForm: Omit<Category, 'id'> = {
    code: '',
    name: '',
    description: '',
    status: 'active',
  };

  protected readonly categories = this.settings.allCategories;

  protected readonly destinationCategories = computed(() => {
    const target = this.deleteTarget();
    if (!target) {
      return [] as Category[];
    }
    return this.categories().filter(
      (c) => c.id !== target.id && c.status === 'active',
    );
  });

  ngOnInit(): void {
    this.settings.refreshFromApi();
  }

  openModal(): void {
    this.editingId.set(null);
    this.resetForm();
    this.showModal.set(true);
  }

  openEdit(row: Category): void {
    this.editingId.set(row.id);
    this.categoryForm = {
      code: row.code,
      name: row.name,
      description: row.description || '',
      status: row.status,
    };
    this.showModal.set(true);
  }

  closeModal(): void {
    this.showModal.set(false);
    this.editingId.set(null);
  }

  save(): void {
    const editingId = this.editingId();

    if (!this.categoryForm.name?.trim()) {
      this.showNotice('Name is required', true);
      return;
    }

    if (editingId) {
      this.settings.updateCategory(editingId, this.categoryForm).subscribe((result) => {
        this.showNotice(result.message, !result.success);
        if (result.success) {
          this.closeModal();
        }
      });
      return;
    }

    this.settings.addCategory(this.categoryForm).subscribe((result) => {
      this.showNotice(result.message, !result.success);
      if (result.success) {
        this.closeModal();
      }
    });
  }

  deleteRow(row: Category): void {
    if (!this.auth.hasPermission('delete_inventory')) {
      this.showNotice('You do not have permission to delete categories.', true);
      return;
    }
    this.deleteTarget.set(row);
    this.deleteImpact.set(null);
    this.deleteError.set('');
    this.targetItemCategoryId = '';
    this.showDeleteModal.set(true);
    this.deleteLoading.set(true);

    this.settings.getCategoryDeleteImpact(row.id).subscribe((impact) => {
      this.deleteLoading.set(false);
      if (!impact) {
        this.deleteError.set('Could not check items in this category. Try again.');
        return;
      }
      this.deleteImpact.set(impact);
      const destinations = this.destinationCategories();
      if (impact.requiresReassignment && destinations.length === 1) {
        this.targetItemCategoryId = destinations[0].id;
      }
    });
  }

  closeDeleteModal(): void {
    if (this.deleteSubmitting()) {
      return;
    }
    this.showDeleteModal.set(false);
    this.deleteTarget.set(null);
    this.deleteImpact.set(null);
    this.deleteError.set('');
    this.targetItemCategoryId = '';
  }

  confirmDelete(): void {
    const target = this.deleteTarget();
    const impact = this.deleteImpact();
    if (!target || !impact || this.deleteSubmitting()) {
      return;
    }

    if (impact.requiresReassignment) {
      const destId = Number(this.targetItemCategoryId);
      if (!destId) {
        this.deleteError.set(
          'Select where items in this category should go.',
        );
        return;
      }
    }

    this.deleteError.set('');
    this.deleteSubmitting.set(true);

    const options = impact.requiresReassignment
      ? { targetItemCategoryId: Number(this.targetItemCategoryId) }
      : undefined;

    this.settings.deleteCategory(target.id, options).subscribe((result) => {
      this.deleteSubmitting.set(false);
      this.showNotice(result.message, !result.success);
      if (result.success) {
        this.closeDeleteModal();
      } else {
        this.deleteError.set(result.message);
      }
    });
  }

  private resetForm(): void {
    this.categoryForm = {
      code: '',
      name: '',
      description: '',
      status: 'active',
    };
  }

  private showNotice(message: string, isError = false): void {
    this.toastIsError.set(isError);
    this.toastMessage.set(message);
    this.showToast.set(true);
    setTimeout(() => this.showToast.set(false), 4000);
  }
}
